import React, { useState } from 'react';
import { dispatch, useSelector } from '@state/store';
import { PriceCalendar } from './PriceCalendar';
import { PriceCalendarFiltersState, PriceCalendarMonthlyState } from '@model/state/price-calendar-state';
import {
  getPriceCalendarFilters,
  getPriceCalendarOccupancyError
} from '@state/slices/price-calendar/filter/priceCalendarFilterSelectors';
import { getPriceCalendarMonthly } from '@state/slices/price-calendar/monthly/priceCalendarMonthlySelectors';
import { setDay } from '@state/slices/price-calendar/monthly/priceCalendarMonthlySlice';
import { GetDepAirportsResponse, ProductApi } from '@model/iceberg';
import { INITIAL_DEAL_FINDER_STATE, setHotelLandingSearch } from '@state/slices/deal-finder/dealFinderSlice';
import { getHotelOnlySearch, getSearchType } from '@state/slices/deal-finder/dealFinderSelectors';
import { performProductSearch } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { getDealFinderLastSearchDate } from '@state/slices/deal-finder-last-searched/dealFinderLastSearchedSelectors';
import { SearchType } from '@model/iceberg/deal-finder/deal-finder';
import { resetSelectedFilters } from '@state/slices/filters';
import { FiltersSideBarType } from '@model/filters';

export interface PriceCalendarContainerProps {
  display: string;
  path: string;
  priceCalendarRef?: any;
  onMonthChange: (month: string, filters: PriceCalendarFiltersState) => void;
  tableOnly?: boolean;
  loading: boolean;
  disabled?: boolean;
  hasUnsaved?: boolean;
  onDayClick?: () => void;
  useDefault?: boolean;
  viewWithFlightsSearch?: () => void;
  isProductLanding?: boolean;
  hasNoRooms?: boolean;
}

export const PriceCalendarContainer = ({
  display,
  path,
  priceCalendarRef,
  onMonthChange,
  tableOnly,
  loading,
  disabled,
  hasUnsaved,
  useDefault,
  onDayClick,
  viewWithFlightsSearch,
  isProductLanding,
  hasNoRooms
}: PriceCalendarContainerProps) => {
  const filters: PriceCalendarFiltersState = useSelector(getPriceCalendarFilters);
  const monthly: PriceCalendarMonthlyState = useSelector(getPriceCalendarMonthly);
  const hasOccupancyError: boolean = useSelector(getPriceCalendarOccupancyError);
  const searchType: SearchType = useSelector(getSearchType);
  const isHotelOnly = useSelector(getHotelOnlySearch);
  const lastSearchDate = useSelector(getDealFinderLastSearchDate);

  const [isSearching, setIsSearching] = useState(false);

  const handleOnDayChange = (day: string) => {
    if (!disabled && !loading && !hasOccupancyError) {
      dispatch(setDay(day));
      dispatch(resetSelectedFilters(FiltersSideBarType.FLIGHTS));
      if (onDayClick) {
        onDayClick();
      }
    }
  };

  const handleOnSubmit = async () => {
    if (!hasOccupancyError) {
      const response: GetDepAirportsResponse = await new ProductApi().getDepAirports(path);
      const airports: Array<string> = response.data || [];
      const { occupancy, airport, duration } = filters;
      const { date, defaultDate } = monthly;
      const searchDate = useDefault ? date || defaultDate : date;
      setIsSearching(true);
      dispatch(setHotelLandingSearch(!!isProductLanding));
      dispatch(
        performProductSearch({
          ...INITIAL_DEAL_FINDER_STATE,
          destinations: [
            {
              display,
              path,
              airports
            }
          ],
          date: searchDate,
          duration,
          airports: isHotelOnly ? [] : [airport],
          occupancy,
          searchType: searchType,
          preferredBoardBasis: filters.board
        })
      );
    }
  };

  const handleOnMonthChange = (month: string) => {
    onMonthChange(month, filters);
  };

  const handleOnFilterChange = (updatedFilters: PriceCalendarFiltersState) => {
    if (updatedFilters.month !== undefined && updatedFilters.month !== null) {
      const monthYear = `${updatedFilters.year}-${updatedFilters.month.toString().padStart(2, '0')}`;
      onMonthChange(monthYear, updatedFilters);
    }
  };

  return (
    <PriceCalendar
      loading={loading || isSearching}
      disabled={disabled}
      priceCalendarRef={priceCalendarRef}
      filters={filters}
      monthly={monthly}
      onDayChange={handleOnDayChange}
      onMonthChange={handleOnMonthChange}
      onFilterChange={handleOnFilterChange}
      onSubmit={handleOnSubmit}
      tableOnly={tableOnly}
      hasUnsaved={hasUnsaved}
      useDefault={useDefault}
      viewWithFlightsSearch={viewWithFlightsSearch}
      lastSearchDate={monthly.date ? undefined : lastSearchDate}
      hasNoRooms={hasNoRooms}
    />
  );
};
